/**
 * Language Utilities
 *
 * Types and utilities for internationalization support
 */

export type LanguageCode = 'en' | 'zh' | 'ar' | 'de' | 'fr' | 'es' | 'pt' | 'nl' | 'it' | 'pl' | 'vi';
export type LanguagePreference = 'auto' | LanguageCode;

export interface Language {
  code: LanguageCode;
  name: string; // Native language name
  flag: string; // Emoji flag
}

export const LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
];

export const DEFAULT_LANGUAGE: LanguageCode = 'en';
export const DEFAULT_PREFERENCE: LanguagePreference = 'auto';
export const LANGUAGE_STORAGE_KEY = 'v-agent-language';

/**
 * Check if a language uses RTL (right-to-left) text direction
 */
export function isRTL(language: LanguageCode): boolean {
  return language === 'ar';
}

/**
 * Get language name in its native script
 */
export function getLanguageName(code: LanguageCode): string {
  const language = LANGUAGES.find((lang) => lang.code === code);
  return language?.name || 'English';
}

/**
 * Get language flag emoji
 */
export function getLanguageFlag(code: LanguageCode): string {
  const language = LANGUAGES.find((lang) => lang.code === code);
  return language?.flag || '🇺🇸';
}

/**
 * Get full language object by code
 */
export function getLanguage(code: LanguageCode): Language {
  return LANGUAGES.find((lang) => lang.code === code) || LANGUAGES[0];
}

/**
 * Validate if a string is a valid language code
 */
export function isValidLanguageCode(code: string): code is LanguageCode {
  return ['en', 'zh', 'ar', 'de', 'fr', 'es', 'pt', 'nl', 'it', 'pl', 'vi'].includes(code);
}

/**
 * Validate if a string is a valid language preference (including 'auto')
 */
export function isValidLanguagePreference(pref: string): pref is LanguagePreference {
  return pref === 'auto' || isValidLanguageCode(pref);
}

/**
 * Detect browser language and map to supported language code
 */
export function detectBrowserLanguage(): LanguageCode {
  if (typeof navigator === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const browserLang = navigator.language.toLowerCase();

  // Map browser language codes to our supported languages
  if (browserLang.startsWith('zh')) return 'zh';
  if (browserLang.startsWith('ar')) return 'ar';
  if (browserLang.startsWith('es')) return 'es';
  if (browserLang.startsWith('pt')) return 'pt';
  if (browserLang.startsWith('fr')) return 'fr';
  if (browserLang.startsWith('it')) return 'it';
  if (browserLang.startsWith('nl')) return 'nl';
  if (browserLang.startsWith('pl')) return 'pl';
  if (browserLang.startsWith('vi')) return 'vi';
  if (browserLang.startsWith('de')) return 'de';

  // Default to English for all other languages
  return 'en';
}
